import { combineEpics, ofType } from 'redux-observable';
import { concatMap, tap } from 'rxjs/operators';
import { pushSupportCaseGAEventToDataLayer } from '../../common/analytics.js';
import { createSupportCasePrivateMethod } from '../../generated/api/uiApiMethods.js';
import { TypeKeys, addSupportCaseFailed, addSupportCaseFulfilled } from '../actions/index.js';
import { callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { actionToActionState, getMdmIdHeaderFromUrlParams } from './epicUtils.js';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { SupportCase } from '../../generated/api/models.js';
import type { ActionTypes, AddSupportCaseAction } from '../actions/index.js';
import type { State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';
import type { AjaxResponse } from 'rxjs/ajax';

export const createSupportCaseObservable = (
  supportCase: SupportCase,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies,
  noSuccessNotification?: boolean,
  showInfoDialog?: boolean,
  successNotificationMsg?: string
) => {
  return callUiApi({
    epicDependencies,
    failureAction: addSupportCaseFailed,
    method: createSupportCasePrivateMethod(),
    payload: supportCase,
    state$,
    successAction: ({ response }: AjaxResponse) =>
      addSupportCaseFulfilled(response, noSuccessNotification, showInfoDialog, successNotificationMsg),
    headers: getMdmIdHeaderFromUrlParams(),
  }).pipe(
    tap(() => {
      pushSupportCaseGAEventToDataLayer('form_submit', true, supportCase.feature);
    })
  );
};

export const addSupportCaseEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.ADD_SUPPORT_CASE)), (action: AddSupportCaseAction) =>
    actionToActionState(action, state$, 'supportCases')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      if (!actionAndState.state) {
        throw new Error('invalid action state for adding support case');
      }
      const { supportCase, noSuccessNotification, showInfoDialog, successNotificationMsg } =
        actionAndState.action as AddSupportCaseAction;
      return createSupportCaseObservable(
        supportCase,
        state$,
        epicDependencies,
        noSuccessNotification,
        showInfoDialog,
        successNotificationMsg
      );
    })
  );

export const supportCaseEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(addSupportCaseEpic);
